import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  get,
} from 'lodash';
import {
  API,
  GlobalActions,
  LocationHelper,
  SidePanelContainer,
  SidePanelHeader,
  SidePanelFooter,
  SidePanelContent,
  SidePanelSection,
  Assignments,
  ToastActions,
  VibeModal,
  VibeTooltip,
  VibeButton,
  VibeIcon,
  viClose,
  viArchive,
  viPlayCircleOutline,
  viUnarchive,
  withRouter,
  color,
} from 'vibeguide';
import Information from './Information';
import LoopDefinition from './LoopDefinition';
import LoopLocations from './LoopLocations';
import './LoopDetails.scss';

class LoopDetails extends PureComponent {
  constructor(props) {
    super(props);

    // Store which values are updated that require an endpoint
    this.updated = {};
    this.origLoop = {};
    this.origLoopSaved = false;

    this.state = {
      confirmArchive: false,
    };
  }

  componentDidUpdate() {
    const {
      loop,
    } = this.props;

    if (!this.origLoopSaved && loop._id) {
      // Save the original Loop
      this.origLoop = loop;
      this.origLoopSaved = true;
    }

    const {
      origLoop,
    } = this;

    if (this.origLoopSaved) {
      // Only track what fields are updated once the original Loop is saved
      if (loop.loops !== origLoop.loops) {
        this.updated.loops = true;
      }
    }
  }

  /**
   * When the locations assigned is clicked
   */
  onClickLocations = () => {
    const {
      user,
      loop: {
        _id,
        companyId,
        locations,
        locationsData,
      },
      setPanel,
      onUpdate,
    } = this.props;

    const allowChanges = user.hasAccessToCompany(companyId)
      && user.can('messagelist.assign_locations')
      && ((_id && user.can('messagelist.modify'))
        || (!_id && user.can('messagelist.create')));

    setPanel({
      extraPanel: {
        width: window.innerWidth,
        show: true,
        children: (
          <LoopLocations
            companyId={companyId}
            locations={locations}
            locationsData={locationsData}
            allowChanges={allowChanges}
            onUpdate={onUpdate}
          />
        ),
      },
    });
  };

  /**
   * Update the template item
   */
  onUpdateItem = (item) => {
    const {
      loop,
      mediaItems,
      onUpdate,
    } = this.props;

    onUpdate({
      ...loop,
      mediaItems: mediaItems.map((i) => {
        if (i._id === item._id) {
          return {
            ...i,
            ...item,
          };
        }

        return i;
      }),
    });
  };

  /**
   * Remove the loop item
   */
  onRemoveItem = (message) => {
    const {
      loop,
      onUpdate,
    } = this.props;

    // update sequence when removing a media item
    const messageRemoved = loop.messages.filter(i => i._id === message.messageId) || [{}];

    const updatedMessages = loop.messages.filter(i => i._id !== message.messageId).map((message1) => {
      if (message1.sequence > messageRemoved[0].sequence) {
        message1.sequence--;
      }
      return message1;
    });

    onUpdate({
      ...loop,
      mediaItems: loop.mediaItems.filter(i => i.messageId !== message.messageId),
      messages: updatedMessages,
    });
  };

  /**
   * Change the item order
   */
  onChangeItemOrder = (data) => {
    const {
      loop,
      mediaItems,
      onUpdate,
    } = this.props;

    const newItems = [];

    mediaItems.forEach((item) => {
      if (item._id !== data.dropItem._id) {
        // ignore the item being dropped (add it before/after the item it was dropped on)
        if (item._id !== data.onItem._id) {
          // not the item dropped on, add the item to the array
          newItems.push(item);
        } else if (item._id === data.onItem._id && data.at === 'above') {
          // dropped on this item (insert before)
          newItems.push(data.dropItem);
          newItems.push(item);
        } else if (item._id === data.onItem._id && data.at === 'below') {
          // dropped on this item (insert after)
          newItems.push(item);
          newItems.push(data.dropItem);
        }
      }
    });

    // add the new sequence numbers to the items
    newItems.forEach((item, index) => {
      item.seq = index;
    });

    // reorder message 'sequence' field in messages in loop
    const messagesReordered = loop.messages.map((message) => {
      const matchingMediaItem = newItems.filter((item) => {
        if (item.messageId === message._id) {
          return true;
        }
        return false;
      });
      message.sequence = matchingMediaItem[0].seq;
      return message;
    });

    onUpdate({
      ...loop,
      mediaItems: newItems,
      messages: messagesReordered,
    });
  };

  /**
   * When the archive button is clicked
   */
  onClickArchive = () => {
    this.setState({
      confirmArchive: true,
    });
  };

  /**
   * When the unarchive button is clicked
   */
  onClickUnarchive = async () => {
    const {
      loop: {
        _id,
        locations,
      },
      onClose,
    } = this.props;

    await API.Loops.reactivate({
      _id,
      locations,
    });
    onClose();

    // tell listening components the object was saved
    document.dispatchEvent(new Event('onSaveLoop'));
  };

  /**
   * When the archive modal is closed
   */
  onCloseArchive = () => {
    this.setState({
      confirmArchive: false,
    });
  };

  /**
   * When the archive modal is confirmed
   */
  onConfirmArchive = async () => {
    const {
      loop: {
        _id,
      },
      onClose,
    } = this.props;

    await API.Loops.deactivate(_id);

    this.onCloseArchive();
    onClose();

    // tell listening components the object was saved
    document.dispatchEvent(new Event('onSaveLoop'));
  };

  /**
   * Save the Loop
   */
  onSave = async () => {
    const {
      // user,
      loop: {
        _id,
        name,
        description,
        companyId,
        locations,
        messages,
        tags,
      },
      history,
      isNew,
      queueToast,
      redirectTo,
    } = this.props;

    document.dispatchEvent(new Event('onSaveLoopStart'));

    queueToast({
      type: 'info',
      title: 'Saving...',
      allowClose: true,
    });

    // Get tags to add/remove
    const modifyTags = tags ? tags.filter(tag => tag.status === 'add' || tag.status === 'remove') : [];
    const loopId = _id || null;

    // Data to create loop
    const data = {
      name,
      description,
      companyId,
      messages,
      tags: modifyTags.map((tag) => {
        return {
          _id: tag._id,
          action: tag.status,
        };
      }),
      locations,
    };

    if (!isNew && loopId) {
      // Editing a Loop, set the ID
      data._id = loopId;
    }

    const filteredMessageData = data.messages.map((message) => {
      return {
        _id: message._id,
        sequence: message.sequence,
      };
    });

    data.messages = filteredMessageData;

    document.dispatchEvent(new Event('onSaveLoop'));

    try {
      const response = !isNew
        ? await API.Loops.modify(loopId, data)
        : await API.Loops.create(data);
      const newLoopId = get(response, '[0].documentId', null);
      const responseType = get(response, '[0].type');

      const success = _id && !isNew
        ? responseType === 'LOOP.UPDATED'
        : responseType === 'LOOP.CREATED';

      if (success) {
        // Successfully saved the loop
        const saveMessage = _id && !isNew
          ? 'Loop Updated!'
          : 'Loop Created!';

        queueToast({
          type: 'success',
          title: saveMessage,
          allowClose: true,
          delay: 500,
        });
      }

      if (isNew) {
        // Redirect user to the edit page for the new item
        const link = `${redirectTo || '/messages/loops'}?loopId=${newLoopId}`;
        history(link);
      }

      // tell listening components the loop was saved
      document.dispatchEvent(new Event('onSaveLoop'));
    } catch (err) {
      // console.error('Error saving loop', response);

      queueToast({
        type: 'error',
        title: 'Error saving loop',
        timeout: 10,
        allowClose: true,
      });
      // document.dispatchEvent(new Event('onSaveLoopError'));
    }
  };

  render() {
    const {
      user,
      loop,
      showDisplayPlayer,
      onClose,
      onUpdate,
      isNew,
      mediaItems,
    } = this.props;

    const {
      confirmArchive,
    } = this.state;

    // ACAF match
    const isACAF = user.isAllLocations({
      companyId: loop.companyId,
      locations: loop.locations,
    });

    const disableSave = !loop.name
      || !loop.companyId
      || (loop.locations && loop.locations.length <= 0)
      || mediaItems.length === 0;

    const displayPlayerDisabled = mediaItems.length === 0;

    const locationCount = LocationHelper.getLocationCount(get(loop, 'locationsData.companies', []), isACAF);
    const exceedsLocations = user.exceedsLocations(loop.locations);

    const disableInput = !user.hasAccessToCompany(loop.companyId)
      || !exceedsLocations
      || (loop._id && !user.can('messagelist.modify'))
      || (!loop._id && !user.can('messagelist.create'));

    // const disableMessages = !isACAF && locationCount === 0;
    const showArchive = user.can('messagelist.delete')
      && user.hasAccessToCompany(loop.companyId)
      && exceedsLocations;
    const isArchived = !loop.active;

    return (
      <SidePanelContainer className="LoopDetails">
        <SidePanelHeader
          icons={(
            <VibeIcon
              className="close"
              icon={viClose}
              color={color.manatee}
              hoverColor={color.obsidian}
              size={24}
              onClick={onClose}
            />
          )}
        >
          <VibeIcon
            icon={viPlayCircleOutline}
            color={color.teal}
            hoverColor={color.teal}
            size={24}
            style={{
              marginRight: 4,
            }}
            tooltip="Preview Loop"
            tooltipProps={{
              placement: 'top',
            }}
            disabled={displayPlayerDisabled}
            onClick={(e) => showDisplayPlayer(e, loop._id, mediaItems)}
          />
          {loop._id ? (
            <div className="flex-horizontal">
              <div className="title">
                <VibeTooltip title={loop.name}>
                  <span>
                    {loop.name}
                  </span>
                </VibeTooltip>
              </div>
            </div>
          ) : (
            <div className="title">
              New Loop
            </div>
          )}
        </SidePanelHeader>

        <SidePanelContent>
          <SidePanelSection title="Information">
            <Information
              name={loop.name}
              description={loop.description}
              isNew={isNew}
              companyName={loop.companyName}
              companyId={loop.companyId}
              tags={loop.tags}
              disableInput={disableInput}
              onUpdate={onUpdate}
              onUploadImage={this.onUploadImage}
            />
          </SidePanelSection>

          <Assignments
            items={[{
              label: 'Locations Assigned',
              count: locationCount.display.element,
              tooltip: locationCount.display.tooltip,
              required: true,
              disabled: !loop.companyId
                || !exceedsLocations,
              warning: !exceedsLocations
                ? 'You do not have access to all the locations assigned to this object'
                : null,
              onClick: loop.companyId && exceedsLocations
                ? this.onClickLocations
                : null,
            }]}
          />

          <SidePanelSection
            title="Loop Definition"
            required
          >
            <LoopDefinition
              mediaItems={mediaItems}
              locations={loop.locations}
              messages={loop.messages}
              onUpdateItem={this.onUpdateItem}
              onRemoveItem={this.onRemoveItem}
              onChangeItemOrder={this.onChangeItemOrder}
              onUpdate={onUpdate}
            />
          </SidePanelSection>
        </SidePanelContent>

        <SidePanelFooter>
          <VibeButton
            text="Save Changes"
            color="secondary"
            loadingEvent="onSaveLoop"
            disabled={disableSave || disableInput}
            onClick={this.onSave}
          />

          {!isNew && (
            <>
              {showArchive && !isArchived && (
                <VibeIcon
                  icon={viArchive}
                  type="button"
                  buttonProps={{
                    size: 32,
                    borderColor: color.error,
                  }}
                  tooltip="Archive"
                  color={color.error}
                  size={20}
                  onClick={this.onClickArchive}
                />
              )}

              {showArchive && isArchived && (
                <VibeIcon
                  icon={viUnarchive}
                  type="button"
                  buttonProps={{
                    size: 32,
                    borderColor: color.success,
                  }}
                  tooltip={locationCount.total > 0
                    ? 'Unarchive'
                    : 'Must have at least 1 active location to unarchive'}
                  color={color.success}
                  size={20}
                  disabled={locationCount.total <= 0}
                  onClick={this.onClickUnarchive}
                />
              )}
            </>
          )}
        </SidePanelFooter>

        <VibeModal
          show={confirmArchive}
          type="confirm"
          title="Archive Loop"
          text={`Are you sure you want to archive ${loop.name}?`}
          confirmProps={{
            color: 'error',
          }}
          onConfirm={this.onConfirmArchive}
          onClose={this.onCloseArchive}
        />
      </SidePanelContainer>
    );
  }
}

LoopDetails.propTypes = {
  /** New Loop */
  isNew: PropTypes.bool,
  loop: PropTypes.oneOfType([
    PropTypes.object,
  ]),
  mediaItems: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func,
  onUpdate: PropTypes.func,
};

LoopDetails.defaultProps = {
  isNew: false,
  loop: {},
  mediaItems: [],
  onClose: () => {},
  onUpdate: () => {},
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

const mapDispatchToProps = {
  setPanel: GlobalActions.setPanel,
  queueToast: ToastActions.queueToast,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(LoopDetails));
